import React, { useMemo } from "react";
import { Link, useParams, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import {
  getRangeVariants,
} from "../config/ranges";
import { getCategoryName } from "../config/categories";
import StatusBadge from "../components/product/StatusBadge";
import VariantCard from "../components/product/VariantCard";
import ProductGalleryPlaceholder from "../components/product/ProductGalleryPlaceholder";
import Footer from "../components/Footer";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { resolveProductSegment } from "../lib/catalogRouting";
import { getPrimaryProductImage } from "../lib/productMedia";

/**
 * Product Range / Family page — master image + variant grid.
 */
export default function ProductRangePage() {
  const { rangeSlug } = useParams();
  const nav = useNavigate();

  const resolved = useMemo(() => resolveProductSegment(rangeSlug), [rangeSlug]);
  const range = resolved.type === "range" ? resolved.range : null;

  useDocumentMeta({
    title: range?.seo?.title || (range ? `${range.name} | DEBSUN` : "Product Range | DEBSUN"),
    description: range?.seo?.description || range?.shortDescription,
  });

  if (resolved.type === "legacy-sku") {
    return <Navigate to={resolved.canonicalPath} replace />;
  }

  if (!range) {
    return (
      <div className="min-h-screen pt-40 text-center" data-testid="range-not-found">
        <div className="display display-section text-4xl">Range not found.</div>
        <Link
          to="/products"
          className="text-[color:var(--gold)] underline text-sm mt-4 inline-block"
        >
          Back to catalogue
        </Link>
      </div>
    );
  }

  const variants = getRangeVariants(range);
  const wattageLine = variants
    .map((v) => v.wattage || v.slug?.toUpperCase())
    .filter(Boolean)
    .join("  —  ");

  return (
    <div className="min-h-screen pt-28" data-testid="product-range-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="range-back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">
            § {range.subcategory || getCategoryName(range.category)}
          </div>
          <StatusBadge status={range.status} />
        </div>

        <h1 className="display display-hero text-4xl md:text-6xl tracking-[-0.03em] max-w-4xl">
          {range.name}
        </h1>
        <p className="mt-5 text-base md:text-lg text-steel-text leading-relaxed max-w-3xl">
          {range.description}
        </p>

        {wattageLine && (
          <div className="mt-6 font-mono-2 text-[12px] md:text-sm tracking-[0.22em] uppercase text-foreground">
            {wattageLine}
          </div>
        )}

        <div className="mt-10 overflow-hidden clip-frame border border-border" data-testid="range-master-image">
          {getPrimaryProductImage(range) ? (
            <img
              src={getPrimaryProductImage(range)}
              alt={`${range.name} range`}
              className="w-full max-h-[560px] object-cover object-center"
            />
          ) : (
            <ProductGalleryPlaceholder label="Range Imagery" />
          )}
        </div>

        {(range.applications?.length > 0 || range.features?.length > 0) && (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {range.applications?.length > 0 && (
              <div>
                <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                  § Applications
                </div>
                <div className="flex flex-wrap gap-2">
                  {range.applications.map((a) => (
                    <span
                      key={a}
                      className="px-3 py-1.5 text-xs border border-border font-mono-2 uppercase tracking-widest"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {range.features?.length > 0 && (
              <div>
                <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                  § Range Notes
                </div>
                <ul className="space-y-2">
                  {range.features.slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-steel-text">
                      <span className="w-1.5 h-1.5 mt-2 bg-[color:var(--gold)] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-16" data-testid="range-variants">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
                § Available Variants
              </div>
              <h2 className="display display-section text-3xl md:text-4xl">
                Select a model
              </h2>
            </div>
            {range.href && (
              <Link
                to={range.href}
                className="inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                style={{ color: "var(--gold)" }}
              >
                Platform Overview <ArrowUpRight size={14} />
              </Link>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {variants.map((p, i) => (
              <VariantCard key={p.sku} product={p} index={i} />
            ))}
          </div>
        </div>

        <div className="mt-16 border border-border bg-secondary/40 p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              Engineering Support
            </div>
            <p className="text-sm text-steel-text leading-relaxed max-w-xl">
              Need help selecting a variant or defining project configuration?
              Our engineering team can advise on application fit.
            </p>
          </div>
          <Link
            to="/#contact"
            className="btn-gold clip-frame-sm px-6 py-4 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2 shrink-0"
          >
            Contact Engineering <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="mt-8">
          <Link
            to="/products"
            className="font-mono-2 text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground"
          >
            ← All product ranges
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
